/**
 * OnboardingModule — the First Hour tab (P13).
 *
 * Surfaces the deterministic Atlas-guide message queue, first-hour beat progress,
 * the Pop-Up City cliffhanger, the hour-2 "Where Your Friends Are / Your Last
 * Ritual" widget, the Safety-Tutorial gate + Rollback tokens (the churn
 * parachutes), Beginner-Mixer rescue for an isolated player, and the plain-language
 * notice once The Lie is revealed. The client RENDERS replicated rows + CALLS
 * reducers; every gate is server-enforced.
 */

import type { App, IModule } from 'enginex';
import type { DbConnection } from '../module_bindings/index.js';
import type { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { iconMarkup } from '../lib/icons.js';

const BEAT_COUNT = 13;

export class OnboardingModule implements IModule {
  readonly name = 'Onboarding';
  private bound = false;
  private els: Record<string, HTMLElement> = {};

  init(_app: App): void {
    for (const id of ['ob-guide', 'ob-progress', 'ob-friends', 'ob-mixers', 'ob-safety', 'ob-lie', 'ob-note']) {
      const el = document.getElementById(id);
      if (el) this.els[id] = el;
    }
    bus.on('atlas:account-ready', () => { this.bind(); this.render(); });
    for (const ev of ['atlas:onboarding-updated', 'atlas:world-entered', 'atlas:orbit-updated', 'atlas:events-updated', 'atlas:wallet-updated'] as const) {
      bus.on(ev, () => this.render());
    }
    this.render();
  }

  private bind(): void {
    if (this.bound) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.bound = true;
    const ping = () => bus.emit('atlas:onboarding-updated', {});
    for (const tbl of [conn.db.onboarding, conn.db.guideMessage, conn.db.beginnerMixerSuggestion]) {
      const t = tbl as unknown as { onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void };
      t.onInsert(ping); t.onUpdate(ping); t.onDelete(ping);
    }
  }

  private render(): void {
    const conn = atlas.conn;
    if (!conn || !conn.identity) return;
    const me = conn.identity;
    this.renderGuide(conn, me);
    this.renderProgress(conn, me);
    this.renderFriends(conn, me);
    this.renderMixers(conn, me);
    this.renderSafety(conn, me);
    this.renderLie(conn, me);
  }

  private renderGuide(conn: DbConnection, me: Identity): void {
    const el = this.els['ob-guide'];
    if (!el) return;
    const mine = [...conn.db.guideMessage.iter()].filter((m) => m.identity.isEqual(me)).sort((a, b) => (a.msgId > b.msgId ? -1 : 1));
    const latest = mine[0];
    // Guide copy is server-authored (the fixed manifest), so it is safe to render.
    el.innerHTML = latest
      ? `<div class="market-item"><div class="head"><span class="kind">${iconMarkup('compass')}The Cosmos Guide</span></div><div class="meta">${this.esc(latest.body)}</div></div>`
      : '<div class="empty">The Cosmos guide will greet you on the Welcome Mat.</div>';
  }

  private renderProgress(conn: DbConnection, me: Identity): void {
    const el = this.els['ob-progress'];
    if (!el) return;
    const o = conn.db.onboarding.identity.find(me);
    if (!o) { el.innerHTML = ''; return; }
    if (o.firstHourComplete) {
      el.innerHTML = `<div class="market-item"><div class="head"><span class="kind">${iconMarkup('globe')}Pop-Up City</span><span class="price">first hour complete</span></div><div class="meta">200 temporary worlds link up in one hour. Will you be a visitor… or a host?</div></div>`;
      return;
    }
    const pct = Math.round(((o.beat + 1) / BEAT_COUNT) * 100);
    el.innerHTML = `<div class="meta">First hour — beat ${o.beat + 1} of ${BEAT_COUNT}</div><div style="height:8px;background:#1c2733;border-radius:4px;overflow:hidden"><div style="height:100%;width:${pct}%;background:#1f6feb"></div></div>`;
  }

  private renderFriends(conn: DbConnection, me: Identity): void {
    const el = this.els['ob-friends'];
    if (!el) return;
    // Hour-2 parachute: "Where Your Friends Are" (a validated Orbit's modal world) +
    // "Your Last Ritual" (the most recent event you hosted).
    const myOrbits = new Set<string>();
    for (const m of conn.db.orbitMember.iter()) if (m.identity.isEqual(me)) myOrbits.add(m.orbitId.toString());
    let friendWorld: bigint | null = null;
    for (const o of conn.db.orbit.iter()) if (myOrbits.has(o.orbitId.toString()) && o.validated && o.preferredWorldId !== 0n) friendWorld = o.preferredWorldId;
    const myEvents = [...conn.db.event.iter()].filter((e) => e.host.isEqual(me)).sort((a, b) => (a.eventId > b.eventId ? -1 : 1));
    const lastRitual = myEvents[0];

    const worldName = (id: bigint): string => this.esc(conn.db.world.worldId.find(id)?.name ?? `world #${id}`);
    el.innerHTML =
      `<div class="meta">${iconMarkup('people')}Where your friends are: ${friendWorld !== null ? worldName(friendWorld) : '<span class="empty">no Orbit yet — Wave to someone nearby</span>'}</div>` +
      `<div class="meta">${iconMarkup('ticket')}Your last ritual: ${lastRitual ? this.esc(lastRitual.title) : '<span class="empty">none yet — try the Event Planner</span>'}</div>`;
  }

  private renderMixers(conn: DbConnection, me: Identity): void {
    const el = this.els['ob-mixers'];
    if (!el) return;
    const sugs = [...conn.db.beginnerMixerSuggestion.iter()].filter((s) => s.identity.isEqual(me));
    const names = new Map<string, string>();
    for (const a of conn.db.account.iter()) names.set(a.identity.toHexString(), a.displayName || a.username);
    if (sugs.length === 0) {
      el.innerHTML = '<div class="empty">No Beginner Mixers suggested. If you feel isolated, ask the guide to find welcoming hosts.</div>' +
        '<button class="action secondary" id="ob-rescue-btn">Find me a Beginner Mixer</button>';
      el.querySelector('#ob-rescue-btn')?.addEventListener('click', () => this.rescue());
      return;
    }
    el.innerHTML = sugs
      .map((s) => `<div class="market-item"><div class="head"><span class="kind">${iconMarkup('people')}Beginner Mixer (event #${s.eventId})</span><span class="price">${s.welcomingScore.toFixed(1)}★</span></div><div class="meta">hosted by ${this.esc(names.get(s.curator.toHexString()) ?? s.curator.toHexString().slice(0, 10) + '…')} — a high-Welcoming Curator</div></div>`)
      .join('');
  }

  private renderSafety(conn: DbConnection, me: Identity): void {
    const el = this.els['ob-safety'];
    if (!el) return;
    const o = conn.db.onboarding.identity.find(me);
    if (!o) { el.innerHTML = ''; return; }
    const tokens = `<div class="meta">${iconMarkup('help')}Rollback tokens: ${o.rollbackTokens} (restore your world to before the last edit)</div>`;
    const safety = o.safetyTutorialDone
      ? `<div class="meta">${iconMarkup('unlock')}Open Build unlocked — you can grant friends build access.</div>`
      : `<div class="meta">${iconMarkup('lock')}Open Build is locked. Complete the Safety Tutorial to grant build access.</div><button class="action" id="ob-safety-btn">Complete Safety Tutorial</button>`;
    el.innerHTML = tokens + safety;
    el.querySelector('#ob-safety-btn')?.addEventListener('click', () => this.safety());
  }

  private renderLie(conn: DbConnection, me: Identity): void {
    const el = this.els['ob-lie'];
    if (!el) return;
    const o = conn.db.onboarding.identity.find(me);
    el.innerHTML = o && o.theLieRevealed
      ? `<div class="market-item"><div class="head"><span class="kind">A note from Cosmos</span></div><div class="meta">Now that you're settled in: hosting new worlds costs more than the trial week, and needs decay faster the deeper you go. Your existing world wasn't re-priced — this is just the honest long game.</div></div>`
      : `<div class="empty">Everything is free and easy while you find your feet.</div>`;
  }

  // ─── Actions ─────────────────────────────────────────────────────────────────

  private safety(): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.note('Completing the Safety Tutorial…', '');
    void conn.reducers.completeSafetyTutorial({}).then(() => this.note('Open Build unlocked.', 'ok')).catch((e: Error) => this.note(e.message, 'err'));
  }

  private rescue(): void {
    const conn = atlas.conn;
    if (!conn || !conn.identity) return;
    this.note('Looking for welcoming hosts…', '');
    void conn.reducers.suggestBeginnerMixers({ identity: conn.identity })
      .then(() => this.note('Beginner Mixers suggested below.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private note(text: string, cls: '' | 'ok' | 'err'): void {
    const el = this.els['ob-note'];
    if (!el) return;
    el.className = cls ? `note ${cls}` : 'note';
    el.textContent = text;
  }

  /** Escape user-controlled strings before interpolating into innerHTML (anti-XSS). */
  private esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'));
  }
}
